import { ApiErrorStatusCode, AuthData, okta } from 'lib-shared-auth';
import { Sentry } from 'lib-shared-sentry';
import { AsyncStatus } from 'lib-shared-types-api';
import type { OktaClientAuthMethod } from 'lib-shared-types-api';
import { useCallback } from 'react';

import { loginApi } from 'apis/login';
import { appActions } from 'app/app.actions';
import { AppLogInType } from 'app/types/app.types';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppNavigate } from 'utils/url/useAppNavigate';

/** Represents the result from the use okta client hook. */
export type UseAuthOktaClientResult =
  | {
      /** The type of the login. */
      readonly type: Extract<AppLogInType, 'oktaClient'>;
    } & (
      | {
          /** The async status og the login. */
          readonly asyncStatus: Extract<AsyncStatus, 'pending'>;
          readonly pendingReason: string;
        }
      | {
          /** The async status. */
          readonly asyncStatus: Extract<AsyncStatus, 'fulfilled'>;
          /** The auth data. */
          readonly authData: AuthData;
        }
    );

/** Represents the trigger for authenticating through okta. */
type Trigger = (
  oktaClientAuthMethod: OktaClientAuthMethod,
) => Promise<UseAuthOktaClientResult>;

/**
 * A hook that resolves the auth data from okta client. This in turn handles the
 * back and forth communication with okta with redirects and login. When the
 * user gets properly logged into okta, the okta tokens are passed to our login
 * api endpoint that exchanges this to auth data.
 */
export const useAuthOktaClient = (): Trigger => {
  const dispatch = useAppDispatch();
  const navigate = useAppNavigate();

  /**
   * Logs in with Okta and gets the auth data for exchanging the Okta token to
   * the auth data. This also triggers redirects in the process.
   */
  const logIn = useCallback<Trigger>(
    async (oktaClientAuthMethod) => {
      const loginResult = await okta.logIn(oktaClientAuthMethod);

      if (loginResult.asyncStatus === 'pending') {
        return Promise.resolve({
          type: 'oktaClient',
          asyncStatus: 'pending',
          pendingReason: loginResult.pendingReason || 'oktaUnknownError',
        });
      }

      // It's a possibility that the tokens could not be fetched after logging
      // into okta. If this happens we redirect to the issuer again.
      if (!loginResult.tokens.accessToken) {
        Sentry.captureEvent({
          message: 'Okta debug - accessToken could not be found.',
          tags: { oktaDebug: true },
          extra: {
            href: window.location.href,
          },
        });

        okta.redirectToIssuer();
        return Promise.resolve({
          type: 'oktaClient',
          asyncStatus: 'pending',
          pendingReason: 'oktaNoAccessToken',
        });
      }

      // We got the okta tokens, proceed with obtaining the dixa access token
      // using the okta access token.
      return dispatch(
        loginApi.endpoints.createOktaLogin.initiate({
          accessToken: loginResult.tokens.accessToken.accessToken,
        }),
      )
        .unwrap()
        .then<UseAuthOktaClientResult>(({ data: authData }) => ({
          type: 'oktaClient',
          asyncStatus: 'fulfilled',
          authData,
        }))
        .catch((error) => {
          if (!error.data) {
            Sentry.captureException(error, {
              tags: { oktaDebug: true },
            });
          }

          okta.clearTokens();

          // We check for unauthorized as the agent could be assigned in okta
          // but not in the dixa organization.
          if (error.status === ApiErrorStatusCode.Unauthorized) {
            navigate(
              {
                path: '/',
                params: {
                  search: {},
                },
              },
              { replace: true },
            );
            dispatch(
              appActions.setError({
                error: { type: 'agentInsufficientRoles' },
              }),
            );
          } else {
            okta.redirectToIssuer();
          }

          return Promise.resolve({
            type: 'oktaClient',
            asyncStatus: 'pending',
            pendingReason: 'oktaFailedToExchangeToken',
          });
        });
    },
    [dispatch, navigate],
  );

  return logIn;
};
